import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Swal from 'sweetalert2';
import { Question } from '../../types';

interface QuestionCardProps {
  question: Question;
  index: number;
  stepNumber: number;
  onEdit: (question: Question) => void;
  onDelete: (question: Question) => void;
  onInsertAt: (stepNumber: number, insertAtOrder: number) => void;
  isDeleting?: boolean;
  showInsertButton?: boolean;
  isSingleStepView?: boolean;
  isDragActive?: boolean;
  isReadOnly?: boolean;
  isPresentMode?: boolean;
  // Client-specific props
  isClientView?: boolean;
  userResponses?: Array<{
    id: number;
    response_text: string;
    interval: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    created_at: string;
  }>;
  onAddResponse?: (questionId: number, responseText: string, interval: string) => Promise<void>;
  onEditResponse?: (responseId: number, responseText: string, interval: string) => Promise<void>;
  onDeleteResponse?: (responseId: number) => Promise<void>;
  onQuestionClick?: (questionId: number, questionOrder: number) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({ 
  question, 
  index,
  stepNumber,
  onEdit,
  onDelete,
  onInsertAt,
  isDeleting = false,
  showInsertButton = false,
  isSingleStepView = false,
  isDragActive = false,
  isReadOnly = false,
  isPresentMode = false,
  isClientView = false,
  userResponses = [],
  onAddResponse,
  onEditResponse,
  onDeleteResponse,
  onQuestionClick
}) => {
  const [isCardHovered, setIsCardHovered] = React.useState(false);
  const [isInsertHovered, setIsInsertHovered] = React.useState(false);
  
  // Client view state
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [newResponseText, setNewResponseText] = React.useState('');
  const [newResponseInterval, setNewResponseInterval] = React.useState<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>(() => {
    const available = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'].filter(interval =>
      !userResponses.some(response => response.interval === interval)
    ) as Array<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>;
    return available.length > 0 ? available[0] : 'daily';
  });
  const [editingResponseId, setEditingResponseId] = React.useState<number | null>(null);
  const [editResponseText, setEditResponseText] = React.useState('');
  const [editResponseInterval, setEditResponseInterval] = React.useState<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>('daily');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [deletingResponseId, setDeletingResponseId] = React.useState<number | null>(null);
  
  // Update new response interval when user responses change
  React.useEffect(() => {
    const usedIntervals = new Set(userResponses.map(response => response.interval));
    const allIntervals: Array<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'> = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
    const availableIntervals = allIntervals.filter(interval => !usedIntervals.has(interval));
    
    if (availableIntervals.length > 0 && !availableIntervals.includes(newResponseInterval)) {
      setNewResponseInterval(availableIntervals[0]);
    }
  }, [userResponses.length, newResponseInterval]); // Re-run when responses or current interval changes
  
  // Always call the hook but conditionally use its properties
  const sortableHook = useSortable({
    id: `question-${question.id}`,
    data: {
      type: 'question',
      question,
    },
    disabled: isReadOnly, // Disable dragging in read-only mode
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = sortableHook;

  // Override listeners and attributes when in read-only mode
  const effectiveListeners = isReadOnly ? {} : listeners;
  const effectiveAttributes = isReadOnly ? {} : attributes;

  // Client view helper functions
  const getIntervalColor = (interval: string) => {
    switch (interval) {
      case 'daily': return 'bg-red-500';
      case 'weekly': return 'bg-orange-500';
      case 'monthly': return 'bg-green-500';
      case 'quarterly': return 'bg-purple-500';
      case 'yearly': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Get intervals that are already used in existing responses
  const getUsedIntervals = (): Set<string> => {
    return new Set(userResponses.map(response => response.interval));
  };

  // Get available intervals for new responses (excluding already used ones)
  const getAvailableIntervals = (): Array<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'> => {
    const allIntervals: Array<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'> = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
    const usedIntervals = getUsedIntervals();
    return allIntervals.filter(interval => !usedIntervals.has(interval));
  };

  const handleAddResponse = async () => {
    if (newResponseText.trim() && onAddResponse && !isSubmitting) {
      try {
        setIsSubmitting(true);
        await onAddResponse(question.id, newResponseText.trim(), newResponseInterval);
        setNewResponseText('');
        // Reset to first available interval
        const availableIntervals = getAvailableIntervals();
        setNewResponseInterval(availableIntervals.length > 0 ? availableIntervals[0] : 'daily');
        setShowAddForm(false);
      } catch (error) {
        console.error('Failed to add response:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleEditResponse = (response: any) => {
    setEditingResponseId(response.id);
    setEditResponseText(response.response_text);
    setEditResponseInterval(response.interval);
  };

  const handleSaveEdit = async () => {
    if (editingResponseId && editResponseText.trim() && onEditResponse && !isSubmitting) {
      try {
        setIsSubmitting(true);
        await onEditResponse(editingResponseId, editResponseText.trim(), editResponseInterval);
        setEditingResponseId(null);
        setEditResponseText('');
        setEditResponseInterval('daily');
      } catch (error) {
        console.error('Failed to update response:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingResponseId(null);
    setEditResponseText('');
    setEditResponseInterval('daily');
  };

  const handleDeleteResponse = async (responseId: number) => {
    if (onDeleteResponse && !deletingResponseId) {
      // Show confirmation dialog
      const result = await Swal.fire({
        title: 'Delete Response?',
        text: 'Are you sure you want to delete this response? This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'rounded-lg',
          confirmButton: 'rounded-md',
          cancelButton: 'rounded-md'
        }
      });

      if (result.isConfirmed) {
        try {
          setDeletingResponseId(responseId);
          await onDeleteResponse(responseId);
          
          // Show success message
          await Swal.fire({
            title: 'Deleted!',
            text: 'Your response has been deleted successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            customClass: {
              popup: 'rounded-lg'
            }
          });
        } catch (error) {
          console.error('Failed to delete response:', error);
          
          // Show error message
          await Swal.fire({
            title: 'Error!',
            text: 'Failed to delete the response. Please try again.',
            icon: 'error',
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'OK',
            customClass: {
              popup: 'rounded-lg',
              confirmButton: 'rounded-md'
            }
          });
        } finally {
          setDeletingResponseId(null);
        }
      }
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsInsertHovered(true)}
      onMouseLeave={() => setIsInsertHovered(false)}
    >
      {/* Insertion border area - minimal spacing - hidden during drag */}
      {!isDragActive && !isReadOnly && (
        <div className="relative mb-1">
          {/* Clickable border line */}
          <div 
            className={`w-full border-t-4 cursor-pointer transition-colors duration-200 ${
              isInsertHovered ? 'border-secondary-400' : 'border-transparent'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onInsertAt(stepNumber, question.question_order);
            }}
            title="Insert Question Here"
          />
          
          {/* Floating insert button - positioned at left edge after question width */}
          {isInsertHovered && (
            <button
              className="absolute top-0 left-0 transform -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-white hover:bg-gray-50 border-2 border-secondary-500 hover:border-secondary-600 rounded-full transition-all duration-200 shadow-md z-20"
              onClick={(e) => {
                e.stopPropagation();
                onInsertAt(stepNumber, question.question_order);
              }}
              title="Insert Question"
            >
              <svg 
                className="w-3 h-3"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#8B5CF6"/>
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Question Card */}
      {isClientView ? (
        isPresentMode ? (
          /* Review Mode - Clean view with response indicators */
            <div
            ref={setNodeRef}
            className={`relative bg-gray-100 mb-4 cursor-pointer rounded-lg border-2 ${
              userResponses.length > 0 
              ? 'border-l-green-500 border-l-4 border-y-transparent border-r-transparent hover:border-l-green-500 hover:border-l-4 hover:border-y-secondary-400 hover:border-r-secondary-400' 
              : 'border-transparent hover:border-secondary-400'
            }`}
            onClick={() => onQuestionClick?.(question.id, index + 1)}
            >
            {/* Question text only */}
            <div className="p-4">
              <h3 className="text-gray-900 font-sm text-sm mb-3">{question.question_text}</h3>
              
              {/* Response indicator */}
              {userResponses.length > 0 ? (
                <div className="text-gray-500 text-xs">
                  ✓ {userResponses.length} response{userResponses.length !== 1 ? 's' : ''}
                </div>
              ) : (
                <div className="text-red-600 text-xs">
                  ⚠ No responses
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Response Mode - Full interactive card */
          <div
            ref={setNodeRef}
            className="relative bg-gray-50 rounded-lg border border-transparent hover:border-gray-300 mb-4 cursor-default"
          >
            {/* Header with question number and question text */}
            <div className="flex items-center py-4 border-b border-gray-300 mx-4">
              <div className="w-8 h-8 bg-secondary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                {index + 1}
              </div>
              <h3 className="text-gray-900 font-medium">{question.question_text}</h3>
            </div>
          
          {/* Content */}
          <div className="p-4">
            {/* My Response header */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 text-sm font-bold">MY RESPONSE</span>
              <span className="bg-gray-300 text-gray-700 text-xs px-2 py-1 rounded-full">
                {userResponses.length} responses
              </span>
            </div>
            
            {/* Response list */}
            <div className="space-y-3 mb-4">
              {userResponses.map((response) => (
                <div key={response.id} className="bg-white border border-gray-200 rounded-lg p-3 relative hover:border-secondary-400">
                  {/* Frequency badge */}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`${getIntervalColor(response.interval)} text-white text-xs px-2 py-1 rounded-full`}>
                      {response.interval.charAt(0).toUpperCase() + response.interval.slice(1)}
                    </span>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEditResponse(response)}
                        className="text-gray-600 hover:text-gray-800 text-sm mx-2"
                        title="Edit response"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteResponse(response.id)}
                        disabled={deletingResponseId === response.id}
                        className={`text-sm mx-2 ${
                          deletingResponseId === response.id 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                        title="Delete response"
                      >
                        {deletingResponseId === response.id ? (
                          <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          '🗑️'
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Response text or edit form */}
                  {editingResponseId === response.id ? (
                    <div className="space-y-3 text-sm">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Interval</label>
                        <select
                          value={editResponseInterval}
                          onChange={(e) => setEditResponseInterval(e.target.value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly')}
                          disabled={true}
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none bg-gray-200 text-gray-500 cursor-not-allowed"
                          title="Interval cannot be changed when editing"
                        >
                          <option value={editResponseInterval}>
                            {editResponseInterval.charAt(0).toUpperCase() + editResponseInterval.slice(1)}
                          </option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your Response</label>
                        <textarea
                          value={editResponseText}
                          onChange={(e) => setEditResponseText(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                          rows={3}
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          disabled={isSubmitting}
                          className={`px-3 py-1 text-sm text-white rounded flex items-center space-x-1 ${
                            isSubmitting 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Saving...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Save Response</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-800 text-sm whitespace-pre-line">{response.response_text}</p>
                  )}
                </div>
              ))}
            </div>
            
            {/* Add Response section */}
            {!showAddForm ? (
              <button
                onClick={() => {
                  const availableIntervals = getAvailableIntervals();
                  if (availableIntervals.length > 0) {
                    setNewResponseInterval(availableIntervals[0]);
                    setShowAddForm(true);
                  }
                }}
                disabled={getAvailableIntervals().length === 0}
                className={`text-sm border-2 border-dashed rounded-lg py-2 px-4 transition-colors duration-200 flex items-center space-x-2 bg-white ${
                  getAvailableIntervals().length === 0
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-purple-600 hover:border-secondary-400 hover:bg-secondary-50'
                }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                <span className={getAvailableIntervals().length === 0 ? 'text-gray-400' : 'text-gray-500'}>
                  {getAvailableIntervals().length === 0 
                    ? 'All intervals used' 
                    : userResponses.length > 0 ? 'Add Another Response' : 'Add Response'
                  }
                </span>
              </button>
            ) : getAvailableIntervals().length > 0 ? (
              <div className="bg-white border border-gray-300 rounded-lg p-4 text-sm">
                <div className="mb-2">
                  <p className="text-xs text-gray-600">
                    Available intervals: {getAvailableIntervals().join(', ')}
                  </p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Interval</label>
                    <select
                      value={newResponseInterval}
                      onChange={(e) => setNewResponseInterval(e.target.value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly')}
                      className="w-full p-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-secondary-500 bg-gray-100"
                    >
                      {getAvailableIntervals().map(interval => (
                        <option key={interval} value={interval}>
                          {interval.charAt(0).toUpperCase() + interval.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Response</label>
                    <textarea
                      value={newResponseText}
                      onChange={(e) => setNewResponseText(e.target.value)}
                      placeholder="Enter your response..."
                      className="w-full p-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-secondary-500"
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewResponseText('');
                        const availableIntervals = getAvailableIntervals();
                        setNewResponseInterval(availableIntervals.length > 0 ? availableIntervals[0] : 'daily');
                      }}
                      className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddResponse}
                      disabled={isSubmitting}
                      className={`px-3 py-1 text-sm text-white rounded flex items-center space-x-1 ${
                        isSubmitting 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Save Response</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* No available intervals message */
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">
                  All interval types have been used. Delete an existing response to add a new one with that interval.
                </p>
              </div>
            )}
          </div>
        </div>
        )
      ) : (
        /* Admin View Card (existing) */
        <div
          ref={setNodeRef}
          style={style}
          {...effectiveAttributes}
          {...effectiveListeners}
          className={`relative bg-white p-4 rounded-sm shadow-sm border border-gray-200 transition-all duration-200 cursor-grab active:cursor-grabbing hover:shadow-md hover:bg-gray-200 mb-1 ${
            isDragging ? 'z-50' : ''
          } ${
            isDeleting 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:border-secondary-300 hover:transform hover:scale-100'
          }`}
          onMouseEnter={() => setIsCardHovered(true)}
          onMouseLeave={() => setIsCardHovered(false)}
        >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
              Q{index + 1}
            </span>
            {question.is_required && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-red-800 bg-red-100">
                {isSingleStepView ? 'Required' : (
                  <svg 
                    width="8" 
                    height="8" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    className="flex-shrink-0"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                )}
              </span>
            )}
          </div>
          {/* Edit and Delete buttons - visible only on hover and not in read-only mode */}
          {!isReadOnly && (
            <div className={`flex items-center space-x-1 transition-opacity duration-200 ${
              isCardHovered ? 'opacity-100' : 'opacity-0'
            }`}>
            <button 
              className="text-xs px-2 py-1 w-12 text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-50 border border-blue-200 hover:border-blue-300 rounded transition-colors duration-200"
              title="Edit question"
              onClick={(e) => {
                e.stopPropagation();
                if (!isDeleting) onEdit(question);
              }}
            >
              Edit
            </button>
            <button 
              className="text-xs px-2 py-1 w-12 text-red-600 hover:text-red-800 bg-white hover:bg-red-50 border border-red-200 hover:border-red-300 rounded transition-colors duration-200"
              title="Delete question"
              onClick={(e) => {
                e.stopPropagation();
                if (!isDeleting) onDelete(question);
              }}
            >
              Del
            </button>
          </div>
          )}
        </div>
        <p className="text-gray-700 text-sm leading-relaxed">
          {question.question_text}
        </p>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;